/**
 * ArchivioSearchBar — F-006 Phase 5 (IMPL-P5-005).
 *
 * Debounced (300ms) free-text input that feeds the FTS `q` parameter. Two
 * supported shapes:
 *
 *  1. Controlled prop API (preferred for tests + reuse):
 *     `<ArchivioSearchBar value={...} onChange={...} isSearching={...} />`
 *
 *  2. Provider-driven (default in the page):
 *     `<ArchivioSearchBar />` → reads/writes `searchTerm` from `useArchivio()`.
 *
 * The debounce uses native `setTimeout` (matches the `IBANInput`/`CFInput`
 * pattern in the profilo feature — no lodash). `aria-label` is required for
 * WCAG: there is no visible <label>, so a programmatic accessible name is
 * the only way screen-reader users learn what the input does.
 */
import { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useArchivioOptional } from '../hooks/useArchivio';

interface ArchivioSearchBarProps {
  /** Controlled value. When omitted, falls back to `useArchivio().searchTerm`. */
  value?: string;
  /** Controlled change handler. When omitted, falls back to provider. */
  onChange?: (value: string) => void;
  /** Visual hint that a network request is in flight. Optional. */
  isSearching?: boolean;
  /** Debounce delay in ms. Default 300ms (matches F-006 spec). */
  debounceMs?: number;
}

export function ArchivioSearchBar({
  value: controlledValue,
  onChange: controlledOnChange,
  isSearching: controlledIsSearching,
  debounceMs = 300,
}: ArchivioSearchBarProps) {
  // Two supported shapes: controlled (tests pass `value`+`onChange`) and
  // provider-driven (the page wraps us in <ArchivioProvider>). We always call
  // `useArchivioOptional()` so the Rules of Hooks are respected — the hook
  // returns `null` when no provider is present (e.g. in unit tests).
  const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;
  const archivioCtx = useArchivioOptional();
  const ctx = isControlled ? null : archivioCtx;

  const value = isControlled ? controlledValue! : (ctx?.searchTerm ?? '');
  const isSearching = isControlled ? !!controlledIsSearching : !!ctx?.isLoading;
  const emit = isControlled ? controlledOnChange! : (ctx?.setSearchTerm ?? (() => {}));

  const [local, setLocal] = useState(value);

  // Keep local in sync if the upstream value changes (e.g. clearFilters()).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // Debounce — only fires `emit` after the user has stopped typing for
  // `debounceMs`. We skip the initial render where `local === value` to avoid
  // a spurious emit on mount.
  const lastEmittedRef = useRef(value);
  useEffect(() => {
    if (local === lastEmittedRef.current) return;
    const t = setTimeout(() => {
      lastEmittedRef.current = local;
      emit(local);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [local, debounceMs, emit]);

  const handleClear = () => {
    setLocal('');
    lastEmittedRef.current = '';
    emit('');
  };

  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Cerca per nome, codice fiscale, indirizzo…"
        aria-label="Cerca nelle pratiche"
        className="pl-9 pr-20"
      />
      <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {isSearching ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : null}
        {local ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleClear}
            aria-label="Cancella ricerca"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
